// L5-MEMBERSHIP-TOPATTEND-001 cycle 2 — ATTENDANCE RANKING GUIDANCE의 multi-period 분기 부재로 인한
// visualizationOnly intention(MONTHLY+ATTENDANCE_LIST_BY+PERIOD) 잘못 선택 차단.
//
// 문제 패턴 (L5-MEMBERSHIP-TOPATTEND-001):
//   사용자: "1월 2월 3월 출석 상위 회원 비교해줘"
//   → aiPrompts.js [ATTENDANCE RANKING GUIDANCE]는 "출석 상위/순위/랭킹/많은/제일 잘 나오는" keyword를
//     CHECK_MEMBER+MONTHLY+ATTENDANCE_LIST_BY+PERIOD + show_attendance_ranking sequence로 라우팅
//   → MONTHLY variant는 visualizationOnly:true — items[]에 attendedCount/attendanceRate 필드 없음
//   → multi-period 비교 시 LLM이 MONTHLY × N period 호출 → merge_slots → show_display 시
//     [VISUALIZATION-ONLY REDIRECT] guard 발동 → show_display 차단 → finish abandon false-ack
//
// 본질 차단:
//   call_api 진입 시 (a) intention이 MONTHLY+ATTENDANCE_LIST_BY+PERIOD (visualizationOnly)
//   + (b) input multi-period 감지 + (c) ATTENDANCE RANKING keyword + (d) comparison/추이 verb 모두 만족 시
//   → non-MONTHLY variant (CHECK_MEMBER+ATTENDANCE_LIST_BY+PERIOD) 자동 치환.
//   non-MONTHLY variant는 per-row attendedCount/attendanceRate 필드 보유 → merge_slots → show_display 정상.
//   keyword set은 aiPrompts.js [ATTENDANCE RANKING GUIDANCE]와 동기화.
//   per (intention, slot) 1회 자동 치환 한정 — 무한 재귀 차단.
//
// 구조 패턴 ([multiPeriodMemberStatusGuide.js] 와 동일 형식 — 일관성 유지).

const HEADER = '[MULTI-PERIOD ATTENDANCE RANKING]';

// MONTHLY+ATTENDANCE_LIST_BY+PERIOD visualization intention (separator 변형 포함).
// ATTENDANCE RANKING GUIDANCE의 SEQUENCE step (1) intention. visualizationOnly:true (id=68).
const WRONG_INTENTION_KEYS = new Set([
  'CHECK_MEMBER+MONTHLY+ATTENDANCE_LIST_BY+PERIOD',
  'CHECK_MEMBER_MONTHLY_ATTENDANCE_LIST_BY_PERIOD',
]);

// non-MONTHLY 변형 — per-row attendedCount/attendanceRate 필드 보유 (id=38).
// merge_slots → show_display 가능 (visualizationOnly 아님).
const REPLACEMENT_INTENTION = 'CHECK_MEMBER+ATTENDANCE_LIST_BY+PERIOD';

// ATTENDANCE RANKING GUIDANCE의 keyword set과 동기화.
// "출석 상위/순위/랭킹/많은/제일 잘 나오는/출석률 상위/높은/좋은" 등 — multi-period 비교 의도 시 발동.
// 가이드가 single-period로 라우팅하는 keyword가 multi-period 입력일 때 non-MONTHLY variant로 자동 치환.
const RANKING_KEYWORDS = Object.freeze([
  '출석 상위', '출석상위',
  '출석 순위', '출석순위',
  '출석 랭킹', '출석랭킹',
  '출석 많은', '출석많은',
  '출석 많이', '출석많이',
  '제일 잘 나오는', '제일잘나오는',
  '잘 나오는 회원', '잘나오는 회원',
  '출석률 상위', '출석률상위',
  '출석률 높은', '출석률높은',
  '출석률 좋은', '출석률좋은',
  '출석률 순위', '출석률순위',
  '출석률 랭킹', '출석률랭킹',
  '출석률 가장 높은', '출석률가장높은',
  '상위 출석', '상위출석',
]);

// Comparison/추이 verb — multi-period 비교 의도 명시 시그널. multi-period 호출 단독으로는 충분 X
// (예: 단일 기간 비교 "2월 출석 상위 회원" — 1개 월만 있으면 multi-period 아님이라 별도 차단됨).
// "비교/추이/변화/순위 비교/랭킹 비교" 등 비교 동사 detect — 단일 키워드는 [ATTENDANCE RANKING GUIDANCE] 우선.
const COMPARISON_VERBS = Object.freeze([
  '비교', '추이', '변화', '대비', '변동',
]);

/**
 * text에서 "N월" 추출 → Set 반환 (multiPeriodMemberStatusGuide.extractMonthSet과 동일 로직).
 *
 * @param {string} text
 * @returns {Set<string>}
 */
export function extractMonthSet(text) {
  const set = new Set();
  if (typeof text !== 'string') return set;
  for (const m of text.matchAll(/(\d{1,2})\s*월/g)) {
    const n = parseInt(m[1], 10);
    if (n >= 1 && n <= 12) set.add(`${n}월`);
  }
  return set;
}

/**
 * 사용자 input + LLM이 호출한 intention 결합 분석.
 * multi-period (N월 2+) + ATTENDANCE RANKING keyword + comparison verb + MONTHLY+ATTENDANCE intention
 * 동시 만족 시 non-MONTHLY 변형 intention 자동 치환 대상 반환.
 *
 * @param {object} ctx
 * @param {string} ctx.userInput
 * @param {string} ctx.intentionKey  — resolved canonical intention key
 * @returns {{
 *   replacementIntention: string,
 *   matchedKeyword: string,
 *   matchedVerb: string,
 *   monthCount: number,
 * } | null}
 */
export function detectMultiPeriodAttendanceRanking(ctx = {}) {
  const { userInput, intentionKey } = ctx;
  if (!userInput || typeof userInput !== 'string') return null;
  if (!intentionKey || !WRONG_INTENTION_KEYS.has(intentionKey)) return null;

  const months = extractMonthSet(userInput);
  if (months.size < 2) return null;

  const matchedKeyword = RANKING_KEYWORDS.find(k => userInput.includes(k));
  if (!matchedKeyword) return null;

  const matchedVerb = COMPARISON_VERBS.find(v => userInput.includes(v));
  if (!matchedVerb) return null;

  return {
    replacementIntention: REPLACEMENT_INTENTION,
    matchedKeyword,
    matchedVerb,
    monthCount: months.size,
  };
}

/**
 * 자동 치환 발동 시 LLM에게 보여줄 안내 prefix (재귀 호출 결과에 prepend).
 *
 * @param {object} ctx
 * @returns {string}
 */
export function formatReplacementMessage(ctx) {
  const { originalIntention, replacementIntention, matchedKeyword, matchedVerb, monthCount } = ctx;
  return (
    `${HEADER} "${originalIntention}"은(는) visualizationOnly:true intention으로 ` +
    `items[]에 attendedCount/attendanceRate 필드가 없어 multi-period merge_slots + show_display 시 차단됩니다. ` +
    `사용자 input에 N월 enumeration (${monthCount}개) + "${matchedKeyword}" keyword + "${matchedVerb}" verb 감지 ` +
    `→ multi-period 출석 랭킹 비교 의도로 "${replacementIntention}" 자동 치환 (per-row attendedCount/attendanceRate 필드 보유). ` +
    `COMPARISON_GUIDE 적용: call_api × N periods (slot=compare_1/2/.../base) → merge_slots(matchKey:"userId", mode:"outer") → ` +
    `sort({field:"{baseLabel}_attendedCount", order:"desc"}) → show_display(per-period attendedCount columns + max_count:20). ` +
    `NEVER call show_attendance_ranking (single month 한계).`
  );
}

export const _HEADER = HEADER;
export const _WRONG_INTENTION_KEYS = WRONG_INTENTION_KEYS;
export const _RANKING_KEYWORDS = RANKING_KEYWORDS;
export const _COMPARISON_VERBS = COMPARISON_VERBS;
export const _REPLACEMENT_INTENTION = REPLACEMENT_INTENTION;
